import { Middleware, isRejectedWithValue } from '@reduxjs/toolkit';
import { toast } from 'sonner';
import { isSessionExpiredError, SESSION_EXPIRED_MESSAGE } from '../../lib/utils/apiResponse';
import { sessionExpired } from '../slices/authSlice';

/**
 * Owns what happens when a session ends on its own.
 *
 * Previously an expired token surfaced as a per-action failure ("Failed to
 * delete product") and the user kept clicking things that could never succeed.
 * Now the session ends once, says why, and sends them to log in with somewhere
 * to come back to.
 *
 * Two things can trigger it: a 401 from any request (this middleware) or the
 * token's own expiry passing while the app is open (AppInitializer's timer).
 * Both funnel through `handleSessionExpiry` so the experience is identical.
 */

/** Where to send the user after they log in again. */
export const RETURN_PATH_KEY = 'returnPathAfterLogin';

/** Guards against a burst of parallel 401s producing a burst of logouts. */
let handlingExpiry = false;

/** Re-arms the handler after a successful sign-in. */
export function resetSessionExpiryGuard(): void {
  handlingExpiry = false;
}

/** Reads and clears the path the user was on when the session ended. */
export function consumeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    if (path) sessionStorage.removeItem(RETURN_PATH_KEY);
    return path;
  } catch {
    return null;
  }
}

function rememberReturnPath(): void {
  try {
    const { pathname, search } = window.location;
    // Returning to an auth page after logging in would just loop.
    if (!/^\/(login|register|forgot-password|reset-password)/.test(pathname)) {
      sessionStorage.setItem(RETURN_PATH_KEY, `${pathname}${search}`);
    }
  } catch {
    // sessionStorage unavailable (private mode) - we lose only the return path.
  }
}

/**
 * Single entry point for ending an expired session. Idempotent: repeat calls
 * while an expiry is already being handled are ignored.
 */
export function handleSessionExpiry(dispatch: (action: any) => unknown): void {
  if (handlingExpiry) return;
  handlingExpiry = true;

  rememberReturnPath();

  // Fixed id so repeats replace the toast instead of stacking.
  toast.error(SESSION_EXPIRED_MESSAGE, { id: 'session-expired', duration: 6000 });

  // The reducer clears the stored token and flips isAuthenticated, which makes
  // the route guards redirect to /login.
  dispatch(sessionExpired());
}

/** Any action that means "a session just started". */
const SESSION_STARTED_ACTIONS = [
  'auth/login/fulfilled',
  'auth/register/fulfilled',
  'auth/setCredentials',
];

export const sessionMiddleware: Middleware = (store) => (next) => (action) => {
  const type = (action as { type?: string })?.type;

  // Re-arm centrally so it doesn't matter which screen signed the user in.
  if (type && SESSION_STARTED_ACTIONS.includes(type)) {
    resetSessionExpiryGuard();
    toast.dismiss('session-expired');
  }

  if (isRejectedWithValue(action) && isSessionExpiredError(action.payload)) {
    handleSessionExpiry(store.dispatch);
  }

  return next(action);
};
